import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { PartyPopper, Users, Candy, GraduationCap, ArrowRight, Check } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 }
  }
};

export const ServicesSection = () => {
  const { t } = useLanguage();

  const services = [
    {
      id: 'birthday',
      title: t('services.birthday.title'),
      description: t('services.birthday.description'),
      icon: PartyPopper,
      color: 'brand-orange',
      bgColor: 'bg-orange-50',
      link: '/book/birthday',
      features: t('services.birthday.features'),
      image: 'https://customer-assets.emergentagent.com/job_kidzone-bookings/artifacts/p735tw3n_IMG_20230409_174029_673.jpg'
    },
    {
      id: 'mascot',
      title: t('services.mascot.title'),
      description: t('services.mascot.description'),
      icon: Users,
      color: 'brand-blue',
      bgColor: 'bg-blue-50',
      link: '/book/mascot',
      features: t('services.mascot.features'),
      image: 'https://customer-assets.emergentagent.com/job_kidzone-bookings/artifacts/yoy7ojm7_mascot%26candy_5.jpg'
    },
    {
      id: 'candy',
      title: t('services.candy.title'),
      description: t('services.candy.description'),
      icon: Candy,
      color: 'brand-pink',
      bgColor: 'bg-pink-50',
      link: '/book/candy-bar',
      features: t('services.candy.features'),
      image: 'https://customer-assets.emergentagent.com/job_kidzone-bookings/artifacts/kpr0jqab_mascot%26candy_3.jpg'
    },
    {
      id: 'stem',
      title: t('services.stem.title'),
      description: t('services.stem.description'),
      icon: GraduationCap,
      color: 'brand-green',
      bgColor: 'bg-green-50',
      link: '/register/stem',
      features: t('services.stem.features'),
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600&auto=format&fit=crop'
    }
  ];

  return (
    <section id="services" data-testid="services-section" className="py-24 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-brand-cream to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-brand-orange/10 text-brand-orange font-heading font-bold text-sm mb-4">
            {t('services.badge')}
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('services.title')}
          </h2>
          <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {services.map((service) => (
            <motion.div
              key={service.id}
              variants={itemVariants}
              className="group relative"
            >
              <div className={`h-full bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 flex flex-col`}>
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent`} />
                  <div className={`absolute top-4 left-4 p-3 rounded-2xl ${service.bgColor}`}>
                    <service.icon className={`w-6 h-6 text-${service.color}`} />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="font-heading text-xl font-bold text-gray-900">
                    {service.title}
                  </h3>
                  <p className="font-body text-gray-600 leading-relaxed mt-3 text-sm">
                    {service.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-2 mt-4">
                    {service.features?.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 font-body text-xs text-gray-600">
                        <Check className={`w-4 h-4 text-${service.color} flex-shrink-0`} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* CTA - pushed to bottom */}
                  <div className="mt-auto pt-6">
                    <Link to={service.link}>
                      <Button 
                        data-testid={`book-${service.id}-btn`}
                        className={`w-full ${
                          service.id === 'birthday' ? 'bg-brand-orange hover:bg-orange-600' :
                          service.id === 'mascot' ? 'bg-brand-blue hover:bg-blue-600' :
                          service.id === 'stem' ? 'bg-green-600 hover:bg-green-700' :
                          'bg-brand-pink hover:bg-pink-600'
                        } text-white rounded-full py-5 font-heading font-bold transition-all hover:scale-105`}
                      >
                        {service.id === 'stem' ? t('services.registerNow') : t('services.bookNow')}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
